import React, { useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

const TYPE_LABELS = {
  PRA_AUFWAND: "Passive RA (Aufwand)",
  PRA_ERTRAG: "Passive RA (Ertrag/Abos)",
  ARA: "Aktive RA",
};

export const AccrualAssistantDialog = ({ companyId, year, yearLocked, onBooked }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [result, setResult] = useState(null);
  const [items, setItems] = useState([]);

  const analyze = async () => {
    setOpen(true);
    setLoading(true);
    setResult(null);
    try {
      const res = await apiClient.get(`/companies/${companyId}/accrual-suggestions`, {
        params: { year: parseInt(year, 10) },
      });
      setResult(res.data);
      setItems((res.data.suggestions || []).map((s, i) => ({
        ...s, _idx: i, selected: false, amountInput: s.amount != null ? String(s.amount) : "",
      })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse fehlgeschlagen");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it) => (it._idx === idx ? { ...it, ...patch } : it)));
  };

  const selectedItems = items.filter((it) => it.selected);
  const canBook = selectedItems.length > 0 &&
    selectedItems.every((it) => parseFloat(it.amountInput) > 0);

  const book = async () => {
    setBooking(true);
    try {
      const res = await apiClient.post(`/companies/${companyId}/accrual-suggestions/book`, {
        year: parseInt(year, 10),
        items: selectedItems.map((it) => ({
          title: it.title,
          accrual_type: it.accrual_type,
          amount: parseFloat(it.amountInput),
          pl_account: it.pl_account,
          reason: it.reason,
        })),
      });
      toast.success(`${res.data.booked} Abgrenzung(en) gebucht — inkl. Auflösung per 01.01.${parseInt(year, 10) + 1}`);
      setOpen(false);
      onBooked?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Buchen fehlgeschlagen");
    } finally {
      setBooking(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-50"
        disabled={yearLocked}
        title={yearLocked ? `Geschäftsjahr ${year} ist definitiv gesperrt` : undefined}
        onClick={analyze}
        data-testid="fs-accrual-assistant-btn"
      >
        <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Abgrenzungs-Assistent
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="accrual-assistant-dialog">
          <DialogHeader>
            <DialogTitle>Abgrenzungs-Assistent — Jahresende {year}</DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="flex items-center gap-2 py-8 justify-center text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              KI analysiert Buchungen rund um den Jahreswechsel {year}/{parseInt(year, 10) + 1} …
            </div>
          )}

          {!loading && result && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                {result.source?.startsWith("llm")
                  ? "KI-Analyse der Buchungen Dez/Jan-Feb. Wähle die zutreffenden Vorschläge, prüfe die Beträge und buche mit einem Klick — die Auflösung per 01.01. wird automatisch mitgebucht."
                  : result.message || "Regelbasierte Vorschläge."}
              </p>

              {items.length === 0 && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600" data-testid="accrual-no-suggestions">
                  Keine Abgrenzungs-Kandidaten gefunden. {result.message || ""}
                </div>
              )}

              {items.map((it) => (
                <label
                  key={it._idx}
                  className={`block border rounded-lg p-3 cursor-pointer transition-colors ${it.selected ? "border-violet-400 bg-violet-50/50" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`accrual-suggestion-${it._idx}`}
                >
                  <div className="flex items-start gap-2.5">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-slate-300"
                      checked={it.selected}
                      onChange={(e) => updateItem(it._idx, { selected: e.target.checked })}
                      data-testid={`accrual-suggestion-check-${it._idx}`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-900">{it.title}</span>
                        <span className="text-[10px] uppercase tracking-wide rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">
                          {TYPE_LABELS[it.accrual_type] || it.accrual_type}
                        </span>
                        {it.confidence != null && (
                          <span className="text-[10px] text-slate-400">{Math.round(it.confidence * 100)}%</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{it.reason}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-slate-500">Konto {it.pl_account} · Betrag CHF</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="h-7 w-32 rounded-md border border-slate-200 px-2 text-sm"
                          value={it.amountInput}
                          placeholder={it.amount_is_estimate ? "Betrag eingeben" : ""}
                          onClick={(e) => e.preventDefault()}
                          onChange={(e) => updateItem(it._idx, { amountInput: e.target.value })}
                          data-testid={`accrual-suggestion-amount-${it._idx}`}
                        />
                        {it.amount_is_estimate && (
                          <span className="text-[10px] text-amber-600">Betrag bitte prüfen/schätzen</span>
                        )}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setOpen(false)}>
              Schliessen
            </Button>
            {items.length > 0 && (
              <Button
                type="button"
                className="rounded-full bg-violet-600 text-white hover:bg-violet-700"
                disabled={!canBook || booking}
                onClick={book}
                data-testid="accrual-book-btn"
              >
                {booking ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                {selectedItems.length > 0
                  ? `${selectedItems.length} Abgrenzung(en) buchen`
                  : "Abgrenzungen wählen"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
